// ============================================================================
// AdminEventAttendanceReportPanel — per-event printable attendance report
// (Task #44, Phase A).
// ============================================================================
// Route: #admin/events/<event_id>/attendance-report. UIF only. Read-only.
// Reached from the "Print attendance report" button on AdminRegistrations.
//
// Rendered OUTSIDE AdminLayout on purpose: the layout's sidebar and top bar
// carry no print: classes, so wrapping this would print the admin nav and fail
// the acceptance test (a clean packet from Ctrl+P). Same bare-return precedent
// as CsvBulkUpload.
//
// Print CSS is a component-local <style> block rather than index.css: Tailwind's
// print: variant cannot express @page or page-break-after, and no shared
// stylesheet in this repo has an @media print block to extend.
//
// Two rules from the hotfix card's §V govern the render:
//   HD-1  guests are participants, not a name list — each gets its own row,
//         its own check-in and its own signature cell.
//   HD-2  a registrant who was never marked has no attendance row at all;
//         "Not marked" must read differently from "Absent" (present=false).
// Signature precedence is data-over-method: if a drawn image is present it
// wins, even when check_in_method says 'signature_typed' (prod has such a row).
// ============================================================================

import { Fragment, useEffect, useState } from 'react';
import { Loader2, AlertCircle, Printer, ArrowLeft } from 'lucide-react';
import { api, type AttendanceReport, type AttendanceReportAttendee, type AttendanceReportSignatory } from '../api';

interface Props {
  token: string;
  eventId: number;
}

const GROUP_LABEL: Record<AttendanceReportAttendee['group'], string> = {
  signed: 'Signed in',
  manual: 'Marked by staff',
  absent: 'Marked absent',
  not_marked: 'Not marked',
};

function fmtDay(d: string | null): string {
  if (!d) return '';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
}

function fmtTime(iso: string | null): string {
  if (!iso) return '';
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return iso;
  return dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function fmtMethod(m: string | null): string {
  if (!m) return '';
  if (m === 'signature_drawn') return 'signed';
  if (m === 'signature_typed') return 'typed';
  return m;
}

// The check-in cell. "Not marked" (no row) is deliberately distinct from
// "Absent" (a row that says present=false).
function CheckInCell({ a }: { a: AttendanceReportSignatory }) {
  if (!a.marked) return <span className="ar-muted">Not marked</span>;
  if (!a.present) return <span className="ar-muted">Absent</span>;
  const t = fmtTime(a.check_in_time);
  const m = fmtMethod(a.check_in_method);
  return <span>{t}{m ? ` (${m})` : ''}</span>;
}

// signature_url is public-origin input: the sign-in endpoint stores it verbatim
// and only length-checks it (validate.ts:452 — max(500000), no format assert).
// SignaturePad emits a PNG data URL, so render only that shape and fall back to
// typed/blank for anything else, rather than handing an arbitrary stored string
// to <img src>. All live signatures are data:image/png;base64,… so nothing real
// is suppressed by this.
const PNG_DATA_URL = /^data:image\/png;base64,[A-Za-z0-9+/=\s]+$/;

function SignatureCell({ a }: { a: AttendanceReportSignatory }) {
  const drawn = a.signature_url && PNG_DATA_URL.test(a.signature_url) ? a.signature_url : null;
  if (!drawn && !a.signature_typed) return <span className="ar-muted">—</span>;

  const sig = drawn
    ? <img src={drawn} alt="signature" className="ar-sig-img" />
    : <span className="ar-typed">{a.signature_typed}</span>;

  // A row can carry a signature AND be marked absent: handleMark (attendance.ts)
  // COALESCEs signature_url/signature_typed, so flipping present=false leaves an
  // existing signature in place. Keep it — it is real evidence the person signed
  // — but label it, so the printed packet can't be misread as "attended".
  if (a.marked && !a.present) {
    return (
      <>
        {sig}
        <span className="ar-sig-note">signed, later marked absent</span>
      </>
    );
  }
  return sig;
}

export function AdminEventAttendanceReportPanel({ token, eventId }: Props) {
  const [report, setReport] = useState<AttendanceReport | null>(null);
  const [error, setError] = useState('');

  // MainApp renders this panel without a key, so navigating between event ids
  // re-renders the SAME instance with a new eventId rather than remounting.
  // Clear the previous report up front (otherwise the old event's packet stays
  // on screen — and printable — until the new fetch resolves), and ignore any
  // in-flight response that lands after eventId has moved on.
  useEffect(() => {
    let cancelled = false;
    setError('');
    setReport(null);
    api.attendanceReport(token, eventId)
      .then((r) => { if (!cancelled) setReport(r); })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Could not load the attendance report.'); });
    return () => { cancelled = true; };
  }, [token, eventId]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-2xl rounded-lg border border-red-200 bg-red-50 p-4 flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">{error}</p>
            <button
              type="button"
              onClick={() => { window.location.hash = '#admin/registrations'; }}
              className="mt-2 text-sm text-red-700 underline"
            >
              Back to registrations
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="ar-root min-h-screen bg-white">
      <style>{`
        .ar-typed { font-family: 'Segoe Script', 'Brush Script MT', cursive; font-size: 15px; }
        .ar-muted { color: #6b7280; }
        .ar-sig-img { max-height: 40px; width: auto; display: block; }
        .ar-sig-note { display: block; font-size: 9px; font-style: italic; color: #6b7280; }
        .ar-table { width: 100%; border-collapse: collapse; }
        .ar-table th, .ar-table td {
          border: 1px solid #d1d5db; padding: 5px 7px;
          font-size: 12px; text-align: left; vertical-align: middle;
        }
        .ar-table th { background: #f3f4f6; font-weight: 600; }
        .ar-guest td:first-child { padding-left: 22px; }
        .ar-grouprow td {
          background: #f9fafb; font-size: 11px; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.04em; color: #4b5563;
        }
        @media print {
          @page { size: letter portrait; margin: 0.5in; }
          .ar-root, .ar-root * {
            color: #000 !important; background: #fff !important;
            box-shadow: none !important;
          }
          .ar-table th { background: #fff !important; }
          .ar-grouprow td { background: #fff !important; color: #000 !important; }
          .ar-muted { color: #000 !important; }
          .ar-sig-note { color: #000 !important; }
          .ar-day { page-break-after: always; }
          .ar-day:last-child { page-break-after: auto; }
          .ar-table { page-break-inside: auto; }
          .ar-table tr { page-break-inside: avoid; }
          .ar-table thead { display: table-header-group; }
        }
      `}</style>

      {/* Screen-only chrome. */}
      <div className="print:hidden border-b border-gray-200 bg-gray-50 px-6 py-3 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => { window.location.hash = '#admin/registrations'; }}
          className="inline-flex items-center gap-1.5 text-sm text-gray-700 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to registrations
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 rounded-md bg-uif-green px-3 py-1.5 text-sm font-semibold text-white hover:bg-uif-green-dark"
        >
          <Printer className="h-4 w-4" />
          Print / save as PDF
        </button>
      </div>

      <div className="px-6 py-5 space-y-8">
        {report.days.map((day) => (
          <section key={day.day_index} className="ar-day space-y-2">
            <header>
              <h1 className="text-lg font-bold">{report.event.title}</h1>
              <p className="text-sm">
                Day {day.day_index}
                {report.event.day_count > 1 ? ` of ${report.event.day_count}` : ''}
                {day.day_date ? ` — ${fmtDay(day.day_date)}` : ''}
              </p>
            </header>

            <table className="ar-table">
              <thead>
                <tr>
                  <th style={{ width: '22%' }}>Name</th>
                  <th style={{ width: '20%' }}>Email</th>
                  <th style={{ width: '12%' }}>Phone</th>
                  <th style={{ width: '16%' }}>Address</th>
                  <th style={{ width: '14%' }}>Check-in</th>
                  <th style={{ width: '16%' }}>Signature</th>
                </tr>
              </thead>
              <tbody>
                {day.attendees.map((a, i) => {
                  const prev = i > 0 ? day.attendees[i - 1] : null;
                  const showGroup = !prev || prev.group !== a.group;
                  return (
                    <Fragment key={`a-${day.day_index}-${a.attendee_id}`}>
                      {showGroup && (
                        <tr className="ar-grouprow">
                          <td colSpan={6}>{GROUP_LABEL[a.group]}</td>
                        </tr>
                      )}
                      <tr>
                        <td>{a.full_name || <span className="ar-muted">—</span>}</td>
                        <td>{a.email || ''}</td>
                        <td>{a.phone || ''}</td>
                        <td>{a.address ? Object.values(a.address).filter(Boolean).join(', ') : ''}</td>
                        <td><CheckInCell a={a} /></td>
                        <td><SignatureCell a={a} /></td>
                      </tr>
                      {/* HD-1: a guest signs for themselves — own check-in, own signature. */}
                      {a.guests.map((g) => (
                        <tr className="ar-guest" key={g.guest_id}>
                          <td>↳ {g.full_name || <span className="ar-muted">—</span>} <span className="ar-muted">(guest)</span></td>
                          <td colSpan={3} />
                          <td><CheckInCell a={g} /></td>
                          <td><SignatureCell a={g} /></td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
                {day.attendees.length === 0 && (
                  <tr>
                    <td colSpan={6} className="ar-muted">No registrants for this event.</td>
                  </tr>
                )}
              </tbody>
            </table>

            <p className="text-[11px] text-gray-500">
              Generated {new Date(report.generated_at).toLocaleString('en-US')}
            </p>
          </section>
        ))}
      </div>
    </div>
  );
}
